import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyntraReturnList,
  deleteMyntraReturn,
} from "app/redux/slice/myntraSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const returnStatusOptions = [
  { value: "Order Exchanged", label: "Order Exchanged", color: "info" },
  { value: "Order Returned", label: "Order Returned", color: "error" },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount || 0);

const EXCLUDED_KEYS = ["_id", "__v", "createdAt", "updatedAt", "deletedAt"];

const formatFieldLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return moment.utc(value).format("DD/MM/YYYY");
  }
  return String(value);
};

const MyntraReturnsList = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const myntraData = useSelector((state) => state?.myntra);
  const returns = myntraData?.returns?.list ?? [];

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [dateRange, setDateRange] = useState([null, null]);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({ status: "" });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [returnDetails, setReturnDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("returnOnDate");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "myntra-returns-list" });

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  useEffect(() => {
    fetchReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filters, dateRange, order, orderBy]);

  const fetchReturns = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getMyntraReturnList(params));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setTempFilters({ status: "" });
    setFilters({ status: "" });
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const getStatusChip = (status) => {
    const cfg = returnStatusOptions.find((o) => o.value === status);
    return (
      <Chip
        label={cfg?.label || status || "-"}
        color={cfg?.color || "default"}
        size="small"
      />
    );
  };

  const handleDeleteReturn = () => {
    dispatch(deleteMyntraReturn(deleteActionProps))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          fetchReturns();
          closeDelete();
        } else {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "error" });
        }
      })
      .catch(() => {});
  };

  const columns = [
    { id: "orderReleaseId", label: "Order ID", sort: false },
    {
      id: "returnOnDate",
      label: "Return Date",
      sort: false,
      render: (row) => (
        <>
          {row?.returnOnDate
            ? moment.utc(row.returnOnDate).format("DD/MM/YYYY")
            : "-"}
        </>
      ),
    },
    // {
    //   id: "productDisplayName",
    //   label: "Product",
    //   sort: false,
    //   render: (row) => (
    //     <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
    //       {row?.productDisplayName}
    //     </Typography>
    //   ),
    // },
    { id: "skuCode", label: "SKU", sort: false },
    { id: "reverseUtrNumber", label: "Reverse UTR", sort: false },
    {
      id: "sellerProductAmount",
      label: "Seller Amount",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "finalSettledAmount",
      label: "Final Settled Amount",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "orderStatus",
      label: "Status",
      sort: false,
      render: (row) => getStatusChip(row?.orderStatus),
    },
    { id: "customerStatecode", label: "State", sort: false },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedReturn(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Myntra Return List
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by order ID, SKU, product..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ButtonGroup size="small" variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.key}
                    onClick={() => setTempDateRange(r.getRange())}
                  >
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={tempFilters.status}
                  onChange={(e) =>
                    setTempFilters((p) => ({ ...p, status: e.target.value }))
                  }
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {returnStatusOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {myntraData?.returns?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Unique Returns (by order line ID):
              </Typography>
              <Chip
                label={myntraData?.returns?.orderCount ?? 0}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Stack>
            <CustomTable
              columns={columns}
              rows={returns}
              page={page}
              rowsPerPage={rowsPerPage}
              count={myntraData?.returns?.total || returns.length}
              Order={order}
              OrderBy={orderBy}
              sortData={(o, ob) => {
                setOrder(o);
                setOrderBy(ob);
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={myntraData?.returns?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedReturn?._id) {
              setReturnDetails(selectedReturn);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            deleteStageAction(selectedReturn?._id);
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Return Details Dialog — dynamic all-fields view */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Details</DialogTitle>
        <DialogContent dividers>
          {returnDetails && (
            <Grid container spacing={2}>
              {Object.entries(returnDetails)
                .filter(([key]) => !EXCLUDED_KEYS.includes(key))
                .map(([key, value], i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2">
                        {formatFieldLabel(key)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatFieldValue(key, value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DialogConfirm
        isOpen={isDelete}
        onClose={closeDelete}
        title="Are you sure you want to delete this return record?"
        onConfirm={handleDeleteReturn}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
      />
    </Box>
  );
};

export default MyntraReturnsList;
